<template>
  <!-- Only show if there are courses available to choose from -->
  <div v-if="allCourses.length > 0">
    <!-- Empty state when no courses selected -->
    <SelectedCourseCard v-if="selectedCourses.length === 0" title="Your schedule" :courses="[]" :show-total="false">
      <template #additional-content>
        <div class="text-center pa-8">
          <SessionIcon :size="48" color="#9E9E9E" class="mb-4" />
          <p class="text-grey mb-0">No courses selected yet</p>
          <p class="text-grey mb-0">Review and manage the courses you've added to your schedule.</p>
        </div>
      </template>
    </SelectedCourseCard>

    <!-- Selected courses list -->
    <SelectedCourseCard
      v-else
      title="Your schedule"
      :courses="formattedCourses"
      :show-delete="true"
      :show-total="true"
      total-label="ESTIMATED TOTAL"
      @remove-course="removeCourse"
    >
      <template #additional-content>
        <!-- Review selection button -->
        <UiButton
          variant="gold"
          size="large"
          @click="next"
          :disabled="selectedCourses.length === 0"
          style="width: 100%"
        >
          Add to schedule
        </UiButton>
        <p
          v-if="shouldShowNoteUnderSchedule"
          class="text-left text-body-2 font-weight-regular mt-4 mb-0"
          style="color: var(--asu-grayscale-gray-3-asu-gray); line-height: 18px"
        >
          <v-icon size="15" color="#747474" class="me-1">mdi-alert</v-icon>
          <strong>Please note:</strong> When you add a course to your schedule, you aren't enrolled until you pay the
          course enrollment fee. Following completion of the course, you can choose to add it to your ASU transcript for
          an additional fee.
        </p>
      </template>
    </SelectedCourseCard>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { Button as UiButton, SelectedCourseCard } from '@/components/ui';
import { SessionIcon } from '@/components/icons';
import { DataLayerMixin } from '@/mixins/dataLayerMixin';
import { Helper } from '@/mixins/helpers';
import { formatDateShort } from '@/utils/dateUtils';
import { buildGa4EcommerceItem, resolveOfferingCanvasCourseId } from '@/utils/ga4Items';
import { useUserStore } from '@/stores/user';
import { getAttributionParams } from '@/util/param-persistence';

export default defineComponent({
  name: 'SelectedCoursesV2',

  components: {
    UiButton,
    SelectedCourseCard,
    SessionIcon
  },

  mixins: [DataLayerMixin, Helper],

  props: {
    selectedCourses: {
      type: Array,
      default: () => []
    },
    shouldShowNoteUnderSchedule: {
      type: Boolean,
      default: false
    },
    allCourses: {
      type: Array,
      default: () => []
    }
  },

  emits: ['remove-course', 'add-course', 'review-selection'],

  computed: {
    // Format courses for SelectedCourseCard component
    formattedCourses() {
      return this.selectedCourses.map((course) => ({
        key: `${course.coursePrefix}-${course.offering.courseOfferingId}`,
        coursePrefix: course.coursePrefixDisplayLabel || course.coursePrefix,
        courseName: course.courseName,
        credits: course.credits,
        price: course.payment?.creditEligibilityPrice || 0,
        startDate: this.getStartDate(course),
        // warning: course.warning?.showPartnershipWarning ? (course.warning?.partnershipWarningMessage || 'This course is not currently covered under your partnership. Fees may apply.') : null,
        // Store original data for removal
        _original: course
      }));
    }
  },

  methods: {
    next() {
      console.log('[SelectedCourses] No session alternative found, proceeding to checkout');

      this.proceedToCheckout();
    },

    proceedToCheckout() {
      console.log('[SelectedCourses] proceedToCheckout called');

      try {
        // Start of data layer changes
        const userStore = useUserStore();
        const leSfContactId = userStore.apis?.learnerData?._contactId || userStore.apis?.learnerData?.contactId || '';
        const { campaign, partner } = getAttributionParams();

        const ecom = {
          event: 'add_to_cart',
          le_sf_contact_id: leSfContactId,
          campaign,
          partner,
          ecommerce: {
            currency: 'USD',
            value: 0,
            items: []
          }
        };

        this.selectedCourses.forEach((course) => {
          const item = buildGa4EcommerceItem({
            canvasCourseId: resolveOfferingCanvasCourseId(course.offering, course.lms),
            offeringId: course.offering?.courseOfferingId,
            courseName: course.courseName,
            coursePrefix: course.coursePrefix,
            interestTrack: course.academicInfo?.interestTrack,
            sessionName: course.term?.sessionName,
            startDate: course.offering?.startDate,
            courseType: course.courseType,
            price: course.payment?.creditEligibilityPrice || 25.0
          });
          ecom.ecommerce.items.push(item);
        });

        ecom.ecommerce.value = ecom.ecommerce.items.length * 25.0;

        console.log('[SelectedCourses] Tracking ecommerce data:', ecom);

        this.asuoDataLayerClick('ecommerceTags', '0', ecom);
        // End of data layer changes
      } catch (error) {
        console.error('[SelectedCourses] Error in data layer tracking:', error);
      }

      console.log('[SelectedCourses] Emitting review-selection event');

      this.$emit('review-selection', this.selectedCourses);
    },

    getStartDate(course) {
      const startDate = course.offering?.startDate;

      // Handle "Anytime" string from backend for OnDemand courses
      if (startDate === 'Anytime') return this.today();

      // Format date for term-based courses
      return formatDateShort(startDate);
    },

    removeCourse(formattedCourse) {
      const course = formattedCourse._original;

      // Clear previous ecommerce object
      if (window.dataLayer) {
        window.dataLayer.push({ ecommerce: null });
      }

      // Data layer tracking
      const userStore = useUserStore();
      const leSfContactId = userStore.apis?.learnerData?._contactId || userStore.apis?.learnerData?.contactId || '';
      const { campaign, partner } = getAttributionParams();

      const ecom = {
        event: 'remove_from_cart',
        le_sf_contact_id: leSfContactId,
        campaign,
        partner,
        ecommerce: {
          currency: 'USD',
          value: 25.0,
          items: [
            buildGa4EcommerceItem({
              canvasCourseId: resolveOfferingCanvasCourseId(course.offering, course.lms),
              offeringId: course.offering?.courseOfferingId,
              courseName: course.courseName,
              coursePrefix: course.coursePrefix,
              interestTrack: course.academicInfo?.interestTrack,
              sessionName: course.term?.sessionName,
              startDate: course.offering?.startDate,
              courseType: course.courseType,
              price: 25.0
            })
          ]
        }
      };

      this.asuoDataLayerClick('ecommerceTags', '0', ecom);

      this.$emit('remove-course', {
        coursePrefix: course.coursePrefix,
        courseStartDate: course.offering.startDate,
        courseOfferingId: course.offering.courseOfferingId,
        courseName: course.courseName
      });
    }
  }
});
</script>
